import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { Transition, UiOnExit } from '@uirouter/angular';
import clone from 'just-clone';
import moment from 'moment';
import {
    combineLatest,
    merge,
    Observable,
    of,
    ReplaySubject,
    Subject,
    BehaviorSubject
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    share,
    skip,
    startWith,
    switchMap,
    take,
    takeWhile,
    tap,
    withLatestFrom,
    shareReplay
} from 'rxjs/operators';
import * as fromSettings from '../../../company-setting';
import * as fromLookups from '../../../lookups';
import { IDirectReport } from '../../../lookups';
import * as fromShared from '../../../shared';
import { Process } from '../../../shared';
import { DialogService } from '../../../shared/efaact-primeng.module';
import { UnsavedChangesService } from '../../../shared/modal-dialog';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import * as fromUser from '../../../user-info';
import { IUserColor, UserRoleEnum } from '../../../user-info';
import { IUserInfo } from '../../../user-info/IUserInfo';
import {
    ExpenseRequestTypeEnum,
    ExpenseStatus,
    TravelTypeEnum
} from '../../expense-enums';
import { IExpenseLine } from '../../IExpenseLine';
import { IExpenseReceipt } from '../../IExpenseReceipt';
import { IExpenseSummary } from '../../IExpenseSummary';
import { IPaymentMethod } from '../../IPaymentMethod';
import * as fromExpense from '../../store';
import { UserPaymentMethod } from '../../UserPaymentMethod';
import { ExpenseEntryFeatureComponent } from '../expense-entry';
import { ExpensePdboFeatureComponent } from '../expense-pdbo-feature';
import {
    computeExpenseDetailCanEdit,
    computeExpenseDetailCanOpen
} from '../../presenters/expense-list/expense-list.component';

@Component({
    selector: 'ef-expense-list-feature',
    templateUrl: './expense-list-feature.component.html',
    styleUrls: ['./expense-list-feature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DialogService],
    standalone: false
})
export class ExpenseListFeatureComponent
    implements OnInit, OnDestroy, AfterViewInit, UiOnExit
{
    // TODO: convert to use process$
    private ProcessSubject = new ReplaySubject<Process>(1);
    private ExpensePopupSubject = new Subject<{
        travelTypeId: TravelTypeEnum;
        summary?: IExpenseSummary;
        detail?: IExpenseLine;
    }>();
    /** Passed into the expense entry dialog; false = read-only review. */
    private popupCanEdit = true;
    private popupTravelTypeId: TravelTypeEnum = TravelTypeEnum.PerDiem;
    private popupSummary: IExpenseSummary;
    private popupDetail: IExpenseLine;
    private _process: Process;
    @Input() public set process(value: Process) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:process:set',
            value
        });
        this._process = value;
        this.ProcessSubject.next(this._process);
    }
    public get process(): Process {
        return this._process;
    }
    process$: Observable<Process>;
    public userInfo$: Observable<IUserInfo>;
    public userInfo: IUserInfo;
    public currentUserSubject = new ReplaySubject<IDirectReport>(1);
    public userWebColors$: Observable<Array<IUserColor>>;
    public expenses$: Observable<Array<IExpenseSummary>>;
    public expenseLines$: Observable<Array<IExpenseLine>>;
    public expenseReceipts$: Observable<Array<IExpenseReceipt>>;
    public expenseReports$: Observable<Array<fromLookups.IDirectReport>>;
    public directReports$: Observable<Array<fromLookups.IDirectReport>>;
    public perDiemTypes$: Observable<Array<fromLookups.IPerDiemLocale>>;
    public travelTypes$: Observable<Array<fromLookups.ITravelType>>;
    public tasks$: Observable<Array<fromLookups.ITask>>;
    public expenseItems$: Observable<Array<fromLookups.IExpenseItem>>;
    public paymentMethods$: Observable<Array<IPaymentMethod>>;
    public userPaymentMethods$: Observable<Array<UserPaymentMethod>>;
    public expenseSummary$: Observable<IExpenseSummary>;
    public selectedDetail$: Observable<IExpenseLine>;
    public selectedUser$: Observable<fromLookups.IDirectReport>;
    public showReimbursedAmount$: Observable<boolean>;
    public failureMessages$: Observable<string>;
    /** StatusDesc / info text returned from ExpenseReportDetail PUT after entry save. */
    public entrySaveMessage: string = null;
    public superUser: boolean;
    public superUserSubject = new ReplaySubject<boolean>(1);
    public selectedDetailSubject = new ReplaySubject<IExpenseLine>(1);
    private active = true;
    private currentUser: IUserInfo;
    /** After entry save: select this SequenceId (edit) or newest line (insert). */
    private pendingSelectSequenceId: number = null;
    private pendingSelectNewestLine = false;
    private dateFilterSubject = new BehaviorSubject<{
        startDate: Date;
        endDate: Date;
    } | null>(null);
    public dateFilter$ = this.dateFilterSubject.asObservable();
    /** Entry bounds for editability (MinWorkDate − lag … MaxWorkDate), same as popup window. */
    public entryDateWindow$: Observable<{
        startDate: Date;
        endDate: Date;
    } | null>;
    /** Same window as `entryDateWindow$`, split for template (single async pipe). */
    public entryDateInputs$: Observable<{
        entryStartDate: Date | undefined;
        entryEndDate: Date | undefined;
    }>;
    private taskFilterSubject = new Subject<Array<fromLookups.ITask>>();
    private expenseItemSubject = new Subject<Array<fromLookups.IExpenseItem>>();
    public directReportsSubject = new Subject<fromLookups.IDirectReport>();
    /**
     * `directReportsSubject` does not replay, so late subscribers (e.g. the
     * post-save refresh) would load summaries without the super user's selected
     * direct report and clear the list. Holds the last real selection.
     */
    private lastDirectReport: fromLookups.IDirectReport = null;
    /** Placeholder EmployeeListID used to clear the summary grid on first load. */
    private static readonly NO_DIRECT_REPORT_ID = '00000000-0000000000';
    private editExpenseSummarySubject = new Subject<IExpenseSummary>();
    private addExpenseSummarySubject = new Subject<{
        summary: IExpenseSummary;
        travelTypeId: TravelTypeEnum;
    }>();
    /** Add a new line to an existing selected report (Detail toolbar buttons). */
    private addExpenseLineSubject = new Subject<{
        summary: IExpenseSummary;
        travelTypeId: TravelTypeEnum;
    }>();
    private selectedExpenseSummarySubject = new ReplaySubject<IExpenseSummary>(
        1
    );
    private editDetailSubject = new Subject<IExpenseLine>();
    private editReceiptSubject = new Subject<IExpenseReceipt>();
    private selectedReceiptSubject = new Subject<IExpenseReceipt>();
    private deleteExpenseSubject = new Subject<IExpenseSummary>();
    private deleteDetailSubject = new Subject<IExpenseLine>();
    private deleteReceiptSubject = new Subject<IExpenseReceipt>();
    private oversignExpenseSubject = new Subject<IExpenseSummary>();
    private ConvertExpenseSubject = new Subject<IExpenseSummary>();
    private ValidateExpenseSubject = new Subject<Array<IExpenseSummary>>();
    private ApproveExpenseSubject = new Subject<Array<IExpenseSummary>>();
    private UnvalidateExpenseSubject = new Subject<Array<IExpenseSummary>>();
    private UnapproveExpenseSubject = new Subject<Array<IExpenseSummary>>();
    private DisapproveExpenseSubject = new Subject<Array<IExpenseSummary>>();
    private editing = false;
    public validating = false;
    public approving = false;
    /** Latest entry window for popup gating (aligned with `entryDateWindow$`). */
    private lastEntryDateWindow: { startDate: Date; endDate: Date } | null = null;
    /** Cached from settings; used before async streams emit on direct-report change. */
    private expenseReportLagDays = 0;
    private canClose = true;
    private dialogRef: fromShared.DynamicDialogRef;
    constructor(
        private store: Store<fromExpense.State>,
        private dispatcher: ActionsSubject,
        private log: LoggingService,
        private confirmservice: UnsavedChangesService,
        public dialogService: DialogService
    ) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent::'
        });
        this.store.dispatch(new fromExpense.ResetSelectedExpense());
        this.store.dispatch(new fromUser.LoadUserInfo());
        this.setupObservables();
        this.loadLookups();
        this.setupSubscriptions();
    }

    ngOnInit() {}
    ngAfterViewInit(): void {
        if (this.process) {
            this.superUserSubject.next(this.process.IsSuperUserProcess);
            if (this.process.ProcessID === 88) {
                this.validating = true;
            }
            if (this.process.ProcessID === 86 || this.process.ProcessID === 152) {
                this.approving = true;
            }
        }
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:ngAfterViewInit',
            process: this.process
        });
    }
    ngOnDestroy() {
        // this is for any observable subscriptions so we don't leak memory
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:ngOnDestroy'
        });
        this.active = false;
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    uiCanExit(transition: Transition) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:uiCanExit',
            transition,
            canClose: this.canClose
        });
        return this.canClose
            ? this.canClose
            : this.confirmservice.confirmPromise();
    }
    selectExpense(summary: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:selectExpense',
            summary
        });
        this.store.dispatch(new fromExpense.ResetSelectedExpense());
        this.store.dispatch(
            new fromExpense.LoadExpenseLines(summary.ExpReportNbr)
        );
        this.store.dispatch(
            new fromExpense.LoadExpenseReceipts(summary.ExpReportNbr)
        );
    }
    loadExpensePopup(summary, detail, receipt, directReport) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:loadExpensePopup',
            summary,
            detail,
            receipt,
            editing: this.editing
        });
        this.store.dispatch(new fromExpense.SetTravelRates(null));
        this.store.dispatch(new fromExpense.SelectTravelRate(null));
        if (!this.editing) {
            if (
                summary.ExpenseReportStatus !== ExpenseStatus.Approved &&
                detail
            ) {
                const canEditResult = this.computeCanEditForSummary(summary);
                this.popupCanEdit = canEditResult.canEdit;
                if (this.shouldOpenExpenseEditPopup(summary)) {
                    this.ExpensePopupSubject.next({
                        travelTypeId:
                            detail?.TravelTypeID || TravelTypeEnum.PerDiem,
                        summary,
                        detail
                    });
                } else {
                    this.log.debug({
                        type: 'ExpenseListFeatureComponent:loadExpensePopup:skipPopup',
                        expReportNbr: summary?.ExpReportNbr,
                        reason: canEditResult.reason,
                        canEdit: canEditResult.canEdit
                    });
                }
            }
        }
        this.store.dispatch(new fromExpense.EditExpenseSummary(summary));
        this.store.dispatch(new fromExpense.EditExpenseDetail(detail));
        this.store.dispatch(new fromExpense.EditExpenseReceipt(receipt || {}));
    }
    setupObservables() {
        // init observables
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(
            select(fromUser.getUserInfo),
            tap(x => (this.userInfo = x))
        );
        this.directReports$ = this.store.pipe(
            select(fromExpense.getExpenseReports),
            takeWhile(() => this.active)
        );
        combineLatest([
            this.superUserSubject, // .pipe(distinctUntilChanged()),
            this.process$, // .pipe(distinctUntilKeyChanged('ProcessID')),
            this.userInfo$ // .pipe(distinctUntilKeyChanged('EmployeeListID'))
        ])
            .pipe(
                // startWith(false),
                debounceTime(500),
                takeWhile(() => this.active),
                tap(([isSuperUser, process, userInfo]) =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent.superUserSubject',
                        isSuperUser,
                        process,
                        userInfo
                    })
                )
            )
            .subscribe(([isSuperUser, process, userInfo]) => {
                this.superUser = isSuperUser;
                if (isSuperUser && process) {
                    setTimeout(() => {
                        this.store.dispatch(
                            new fromLookups.LoadDirectReports({
                                user: userInfo,
                                activeOnly: true,
                                processId: process.ProcessID
                            })
                        );
                    }, 0);

                    this.directReports$ = this.store.pipe(
                        select(fromLookups.getDirectReportsLookup),
                        filter(drs => Array.isArray(drs) && drs.length > 0),
                        map(drs => clone(drs)),
                        map(drs =>
                            drs
                                .filter(x => x.IsActive)
                                .filter(x => x.IsValidUser)
                                .sort((a, b) =>
                                    a.EmployeeName.localeCompare(b.EmployeeName)
                                )
                        ),
                        takeWhile(() => this.active)
                    );
                } else {
                    this.directReports$ = this.store.pipe(
                        select(fromExpense.getExpenseReports),
                        filter(drs => Array.isArray(drs) && drs.length > 0),
                        map(drs => clone(drs)),
                        map(drs =>
                            drs.sort((a, b) =>
                                a.EmployeeName.localeCompare(b.EmployeeName)
                            )
                        ),
                        takeWhile(() => this.active)
                    );
                }
            });
        this.expenseLines$ = this.store.pipe(
            select(fromExpense.getExpenseLines)
        );
        this.expenseReceipts$ = this.store.pipe(
            select(fromExpense.getExpenseReceipts)
        );
        this.expenseReports$ = this.store.pipe(
            select(fromExpense.getExpenseReports),
            map(x => x.map(y => ({ ...y }))),
            map(x =>
                x.sort((a, b) =>
                    a.EmployeeName.toLocaleLowerCase().localeCompare(
                        b.EmployeeName.toLocaleLowerCase()
                    )
                )
            ),
            tap(expenseReports =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent.expenseReports$',
                    expenseReports
                })
            )
        );

        this.perDiemTypes$ = this.store.pipe(
            select(fromLookups.getPerDiemTypesLookup)
        );
        this.travelTypes$ = this.store.pipe(
            select(fromLookups.getTravelTypesLookup)
        );
        this.tasks$ = this.store.pipe(select(fromLookups.getTasksLookup));
        this.paymentMethods$ = this.store.pipe(
            select(fromExpense.getExpensePaymentMethods)
        );
        this.userPaymentMethods$ = this.store.pipe(
            select(fromExpense.getUserPaymentMethods)
        );
        this.expenseItems$ = this.store.pipe(
            select(fromLookups.getExpenseItems)
        );
        this.expenseSummary$ =
            this.selectedExpenseSummarySubject.asObservable();
        this.selectedUser$ = this.store.pipe(
            select(fromExpense.getSelectedUser),
            tap(selectedUser =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent.selectedUser$',
                    selectedUser
                })
            )
        );
        merge(
            this.userInfo$.pipe(filter(x => !!(x && x.EmployeeListID))),
            this.selectedUser$.pipe(filter(x => !!(x && x.EmployeeListID)))
        )
            .pipe(
                withLatestFrom(this.directReports$, this.process$),
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent.userInfo$,selectedUser$,directReports$',
                        x
                    })
                )
            )
            .subscribe(([userInfo, directReports, process]) => {
                const user = userInfo as IUserInfo;
                const dr = userInfo as IDirectReport;
                if (this.isExpenseSuperUser()) {
                    // Super user: use the selected direct report's entry period only.
                    if (dr?.EmployeeListID && dr.MinWorkDate && dr.MaxWorkDate) {
                        const resolved = {
                            id: dr.EmployeeListID,
                            EmployeeListID: dr.EmployeeListID,
                            EmployeeName: dr.EmployeeName,
                            MinWorkDate: dr.MinWorkDate,
                            MaxWorkDate: dr.MaxWorkDate
                        } as IDirectReport;
                        this.currentUserSubject.next(resolved);
                        this.onUserSelected(resolved);
                    } else if (dr?.EmployeeListID && directReports?.length) {
                        const founddr = directReports.find(
                            x => x.EmployeeListID === dr.EmployeeListID
                        );
                        if (founddr?.MinWorkDate && founddr?.MaxWorkDate) {
                            const resolved = { ...founddr };
                            this.currentUserSubject.next(resolved);
                            this.onUserSelected(resolved);
                        }
                    }
                    return;
                }
                const next = {
                    id: userInfo && userInfo.EmployeeListID,
                    EmployeeListID: userInfo && userInfo.EmployeeListID,
                    EmployeeName: userInfo && userInfo.EmployeeName,
                    MinWorkDate: null,
                    MaxWorkDate: null
                };
                if (user && user.UserEntryStartDate) {
                    next.MinWorkDate = user.UserEntryStartDate;
                    next.MaxWorkDate = user.UserEntryEndDate;
                    this.onUserSelected(next);
                }
                if (!next.MinWorkDate && dr && dr.MinWorkDate) {
                    next.MinWorkDate = dr.MinWorkDate;
                    next.MaxWorkDate = dr.MaxWorkDate;
                    this.onUserSelected(next);
                }
                if (!next.MinWorkDate) {
                    const founddr = directReports.find(
                        x => x.EmployeeListID === dr.EmployeeListID
                    );
                    if (founddr) {
                        next.MinWorkDate = founddr.MinWorkDate;
                        next.MaxWorkDate = founddr.MaxWorkDate;
                        this.onUserSelected(next);
                    }
                }
                // For Expense Approval (86) / 152, preserve user-selected date range when
                // selecting a person from the dropdown so dates don't revert and reports don't flash.
                const currentDateFilter = this.dateFilterSubject.getValue();
                if (
                    (process?.ProcessID === 86 || process?.ProcessID === 152) &&
                    currentDateFilter != null
                ) {
                    next.MinWorkDate = currentDateFilter.startDate;
                    next.MaxWorkDate = currentDateFilter.endDate;
                }
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent.userInfo$,selectedUser$',
                    next
                });

                this.currentUserSubject.next(next);
            });
        this.userWebColors$ = this.store.pipe(
            select(fromUser.getUserColorsSorted)
        );
        this.showReimbursedAmount$ = combineLatest([
            this.store.pipe(select(fromSettings.isGCPEnabled)),
            this.store.pipe(select(fromSettings.isReimburseViaPayrollEnabled))
        ]).pipe(
            tap(([gcp, reimb]) => this.log.verbose({
                type: 'ExpenseListFeatureComponent:showReimbursedAmount$',
                gcp,
                reimb
            })),
            map(([gcp, reimb]) => !!gcp && !!reimb),
            takeWhile(() => this.active)
        );
        this.failureMessages$ = this.store.pipe(
            select(fromExpense.getFailureMessages)
        );

        const expenseReportLagDaysForWindow$ = this.store.pipe(
            select(fromSettings.selectEntity('ExpenseReportLagDays')),
            map(setting => {
                const rawValue = setting?.SettingValue;
                const lagDays =
                    rawValue != null && rawValue !== ''
                        ? parseInt(String(rawValue), 10)
                        : 0;
                const lag = isNaN(lagDays) ? 0 : lagDays;
                this.expenseReportLagDays = lag;
                this.log.debug({
                    type: 'ExpenseListFeatureComponent:expenseReportLagDaysForWindow$',
                    settingType: setting?.SettingType,
                    rawSettingValue: rawValue,
                    lagDaysComputed: lag
                });
                return lag;
            })
        );
        this.entryDateWindow$ = combineLatest([
            this.directReportsSubject.pipe(
                startWith(null as IDirectReport | null)
            ),
            this.userInfo$.pipe(startWith(null as IUserInfo | null)),
            expenseReportLagDaysForWindow$,
            this.superUserSubject.pipe(startWith(false))
        ]).pipe(
            map(([dr, user, lag, isSuperUser]) => {
                this.log.debug({
                    type: 'ExpenseListFeatureComponent:entryDateWindow$:sources',
                    lagDays: lag,
                    directReportSource: dr
                        ? {
                              EmployeeListID: dr.EmployeeListID,
                              EmployeeName: dr.EmployeeName,
                              MinWorkDate: dr.MinWorkDate,
                              MaxWorkDate: dr.MaxWorkDate
                          }
                        : null,
                    userInfoSource: user
                        ? {
                              EmployeeListID: user.EmployeeListID,
                              UserEntryStartDate: user.UserEntryStartDate,
                              UserEntryEndDate: user.UserEntryEndDate
                          }
                        : null
                });
                const effective = this.resolveDirectReportForEntryWindow(
                    dr,
                    user,
                    isSuperUser || this.isExpenseSuperUser()
                );
                const window = this.computeEntryDateWindow(effective, lag);
                this.log.debug({
                    type: 'ExpenseListFeatureComponent:entryDateWindow$:emit',
                    lagDays: lag,
                    effectiveSnapshot: effective
                        ? {
                              EmployeeListID: effective.EmployeeListID,
                              MinWorkDate: effective.MinWorkDate,
                              MaxWorkDate: effective.MaxWorkDate
                          }
                        : null,
                    computedWindow: window
                        ? {
                              startDate: window.startDate,
                              endDate: window.endDate,
                              startIso: moment(window.startDate).toISOString(),
                              endIso: moment(window.endDate).toISOString()
                          }
                        : null
                });
                return window;
            }),
            distinctUntilChanged((a, b) => {
                if (a == null && b == null) {
                    return true;
                }
                if (a == null || b == null) {
                    return false;
                }
                return (
                    a.startDate.getTime() === b.startDate.getTime() &&
                    a.endDate.getTime() === b.endDate.getTime()
                );
            }),
            tap(w =>
                this.log.debug({
                    type: 'ExpenseListFeatureComponent:entryDateWindow$:distinct',
                    w:
                        w == null
                            ? null
                            : {
                                  startIso: moment(w.startDate).toISOString(),
                                  endIso: moment(w.endDate).toISOString()
                              }
                })
            ),
            shareReplay(1)
        );
        this.entryDateInputs$ = this.entryDateWindow$.pipe(
            map(w => ({
                entryStartDate: w?.startDate,
                entryEndDate: w?.endDate
            })),
            tap(mapped =>
                this.log.debug({
                    type: 'ExpenseListFeatureComponent:entryDateInputs$:mapped',
                    entryStartIso: mapped.entryStartDate
                        ? moment(mapped.entryStartDate).toISOString()
                        : null,
                    entryEndIso: mapped.entryEndDate
                        ? moment(mapped.entryEndDate).toISOString()
                        : null
                })
            ),
            startWith({
                entryStartDate: undefined,
                entryEndDate: undefined
            }),
            distinctUntilChanged((a, b) => {
                const ta = a.entryStartDate?.getTime();
                const tb = b.entryStartDate?.getTime();
                const ua = a.entryEndDate?.getTime();
                const ub = b.entryEndDate?.getTime();
                return ta === tb && ua === ub;
            }),
            tap(out =>
                this.log.debug({
                    type: 'ExpenseListFeatureComponent:entryDateInputs$:distinct',
                    entryStartIso: out.entryStartDate
                        ? moment(out.entryStartDate).toISOString()
                        : null,
                    entryEndIso: out.entryEndDate
                        ? moment(out.entryEndDate).toISOString()
                        : null
                })
            ),
            shareReplay(1)
        );

        this.entryDateWindow$
            .pipe(takeWhile(() => this.active))
            .subscribe(w => {
                this.lastEntryDateWindow = w;
            });

        this.selectedDetail$ = this.store.pipe(
            select(fromExpense.getEditingExpenseDetail)
        );
    }

    private computeCanEditForSummary(summary: IExpenseSummary) {
        return computeExpenseDetailCanEdit({
            validating: this.validating,
            approving: this.approving,
            expenseReportStatus: summary?.ExpenseReportStatus,
            reportDateRaw: summary?.ReportDate,
            entryStartDate: this.lastEntryDateWindow?.startDate,
            entryEndDate: this.lastEntryDateWindow?.endDate
        });
    }

    /**
     * Opens the modal for edit or read-only review (validation/approval may open when `canEdit` is false).
     */
    private shouldOpenExpenseEditPopup(summary: IExpenseSummary): boolean {
        const canOpen = computeExpenseDetailCanOpen({
            validating: this.validating,
            approving: this.approving,
            expenseReportStatus: summary?.ExpenseReportStatus,
            reportDateRaw: summary?.ReportDate,
            entryStartDate: this.lastEntryDateWindow?.startDate,
            entryEndDate: this.lastEntryDateWindow?.endDate
        });
        const canEditResult = this.computeCanEditForSummary(summary);
        this.log.debug({
            type: 'ExpenseListFeatureComponent:shouldOpenExpenseEditPopup',
            expReportNbr: summary?.ExpReportNbr,
            canOpen,
            canEdit: canEditResult.canEdit,
            reason: canEditResult.reason,
            inWindow: canEditResult.inWindow,
            reportDateIso: canEditResult.reportDateIso
        });
        return canOpen;
    }

    /**
     * Prefer direct report Min/Max when present; otherwise use the signed-in user's entry period.
     */
    private isExpenseSuperUser(): boolean {
        return !!(this.superUser || this.process?.IsSuperUserProcess);
    }

    private resolveDirectReportForEntryWindow(
        dr: IDirectReport | null,
        user: IUserInfo | null,
        isSuperUser = false
    ): IDirectReport | null {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:resolveDirectReportForEntryWindow:inputs',
            drSnapshot: dr
                ? {
                      EmployeeListID: dr.EmployeeListID,
                      MinWorkDate: dr.MinWorkDate,
                      MaxWorkDate: dr.MaxWorkDate
                  }
                : null,
            userSnapshot: user
                ? {
                      EmployeeListID: user.EmployeeListID,
                      UserEntryStartDate: user.UserEntryStartDate,
                      UserEntryEndDate: user.UserEntryEndDate
                  }
                : null
        });
        if (!user) {
            this.log.debug({
                type: 'ExpenseListFeatureComponent:resolveDirectReportForEntryWindow:result',
                reason: 'noUser',
                resolved: dr
            });
            return dr;
        }
        const hasDrBounds =
            dr &&
            dr.MinWorkDate != null &&
            dr.MaxWorkDate != null;
        if (hasDrBounds) {
            this.log.debug({
                type: 'ExpenseListFeatureComponent:resolveDirectReportForEntryWindow:result',
                reason: 'directReportBounds',
                resolved: dr
            });
            return dr;
        }
        if (
            !isSuperUser &&
            user.UserEntryStartDate &&
            user.UserEntryEndDate
        ) {
            const resolved = {
                ...(dr || {}),
                EmployeeListID: dr?.EmployeeListID ?? user.EmployeeListID,
                EmployeeName: dr?.EmployeeName ?? user.EmployeeName,
                MinWorkDate: moment(user.UserEntryStartDate).toISOString(),
                MaxWorkDate: moment(user.UserEntryEndDate).toISOString()
            } as IDirectReport;
            this.log.debug({
                type: 'ExpenseListFeatureComponent:resolveDirectReportForEntryWindow:result',
                reason: 'userEntryPeriod',
                resolvedMinMax: {
                    MinWorkDate: resolved.MinWorkDate,
                    MaxWorkDate: resolved.MaxWorkDate
                }
            });
            return resolved;
        }
        this.log.debug({
            type: 'ExpenseListFeatureComponent:resolveDirectReportForEntryWindow:result',
            reason: 'fallbackDirectReport',
            resolved: dr
        });
        return dr;
    }

    private computeEntryDateWindow(
        effective: IDirectReport | null,
        lagDays: number
    ): { startDate: Date; endDate: Date } | null {
        if (!effective?.MinWorkDate || !effective?.MaxWorkDate) {
            this.log.debug({
                type: 'ExpenseListFeatureComponent:computeEntryDateWindow:result',
                reason: 'missingMinOrMax',
                effectiveMin: effective?.MinWorkDate,
                effectiveMax: effective?.MaxWorkDate,
                lagDaysInput: lagDays
            });
            return null;
        }
        const lag = isNaN(lagDays) ? 0 : lagDays;
        const startDate = moment(effective.MinWorkDate)
            .subtract(lag, 'days')
            .startOf('day')
            .toDate();
        const endDate = moment(effective.MaxWorkDate).endOf('day').toDate();
        this.log.debug({
            type: 'ExpenseListFeatureComponent:computeEntryDateWindow:result',
            reason: 'computed',
            sourceMinWorkDate: effective.MinWorkDate,
            sourceMaxWorkDate: effective.MaxWorkDate,
            lagDaysApplied: lag,
            minWorkMomentIso: moment(effective.MinWorkDate).toISOString(),
            maxWorkMomentIso: moment(effective.MaxWorkDate).toISOString(),
            windowStartIso: moment(startDate).toISOString(),
            windowEndIso: moment(endDate).toISOString()
        });
        return { startDate, endDate };
    }

    /**
     * When no filter range is set yet (first super-user pick), seed Start/End
     * from the direct report's entry window so getNewExpenses$ can load.
     * Do not overwrite dates the user already set in the Filters section.
     */
    private syncDateFilterFromDirectReport(dr: IDirectReport): void {
        if (this.dateFilterSubject.getValue() != null) {
            return;
        }
        const window = this.computeEntryDateWindow(
            dr,
            this.expenseReportLagDays
        );
        if (!window) {
            return;
        }
        this.log.debug({
            type: 'ExpenseListFeatureComponent:syncDateFilterFromDirectReport',
            dr: {
                EmployeeListID: dr.EmployeeListID,
                MinWorkDate: dr.MinWorkDate,
                MaxWorkDate: dr.MaxWorkDate
            },
            window: {
                startIso: moment(window.startDate).toISOString(),
                endIso: moment(window.endDate).toISOString()
            }
        });
        this.onDateFiltersChanged(window);
    }

    /**
     * Single funnel for direct-report emissions so the last real selection stays
     * available after the subject has emitted. The placeholder id used to clear
     * the grid is not a selection.
     */
    private emitDirectReport(dr: fromLookups.IDirectReport): void {
        if (
            dr?.EmployeeListID &&
            dr.EmployeeListID !==
                ExpenseListFeatureComponent.NO_DIRECT_REPORT_ID
        ) {
            this.lastDirectReport = dr;
        }
        this.directReportsSubject.next(dr);
    }

    showExpensePopup(directReport, process) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:showExpensePopup',
            directReport,
            process,
            travelTypeId: this.popupTravelTypeId,
            summary: this.popupSummary,
            detail: this.popupDetail
        });
        if (!process) {
            this.log.warn({
                type: 'ExpenseListFeatureComponent:showExpensePopup',
                message: 'Cannot open expense entry: process is not loaded yet'
            });
            return;
        }
        if (this.dialogRef) {
            return;
        }

        // Set before DialogService.open so filter-driven ShowExpenseDetail(null)
        // cannot clear selection and hide Detail Add buttons.
        this.editing = true;
        this.entrySaveMessage = null;

        this.dialogRef = this.dialogService.open(ExpenseEntryFeatureComponent, {
            header: 'Expense Entry',
            closable: false,
            closeOnEscape: false,
            showHeader: false,
            modal: true,
            autoZIndex: true,
            styleClass: 'expense-entry-dialog',
            data: {
                process: process,
                superUser: this.isExpenseSuperUser(),
                userInfo: this.userInfo,
                directReport: directReport,
                canEdit: this.popupCanEdit,
                travelTypeId: this.popupTravelTypeId,
                summary: this.popupSummary,
                detail: this.popupDetail
            }
        });
        this.dialogRef.onClose.pipe(take(1)).subscribe(value => {
            this.editing = false;
            this.dialogRef = null;
            this.log.verbose({
                type: 'ExpenseEntryFeatureComponent:afterClosed',
                value,
                popupSummary: this.popupSummary
            });
            if (value?.saved) {
                this.entrySaveMessage = value.message || null;
                const seq = this.popupDetail?.SequenceId;
                this.pendingSelectSequenceId =
                    seq != null && seq > 0 ? seq : null;
                this.pendingSelectNewestLine =
                    this.pendingSelectSequenceId == null;
                this.refreshExpenseListAfterSave(this.popupSummary);
            } else if (
                this.popupSummary?.ExpReportNbr != null &&
                this.popupSummary.ExpReportNbr > 0
            ) {
                // Keep selection if the user cancelled
                this.selectedExpenseSummarySubject.next(this.popupSummary);
            }
        });
    }

    /**
     * Reload summary list after a successful save without clearing selection.
     * Keeps the prior report selected when editing; for a new report (-1),
     * selects the newly created summary once the list returns.
     */
    private refreshExpenseListAfterSave(priorSummary: IExpenseSummary): void {
        const dateFilters =
            this.dateFilterSubject.getValue() || this.lastEntryDateWindow;
        const keepNbr =
            priorSummary?.ExpReportNbr != null && priorSummary.ExpReportNbr > 0
                ? priorSummary.ExpReportNbr
                : null;

        // Use the tracked selection, not the subject: it does not replay, so a
        // super user's direct report would be lost here and the reload would
        // return no rows.
        const dr = this.lastDirectReport;

        combineLatest([
            this.userInfo$.pipe(filter(u => !!u?.EmployeeListID), take(1)),
            this.process$.pipe(filter((p): p is Process => !!p), take(1)),
            this.store.pipe(select(fromExpense.getExpenseSummaries), take(1))
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([user, process, priorList]) => {
                const priorMax = (priorList || []).reduce(
                    (max, x) => Math.max(max, x?.ExpReportNbr || 0),
                    0
                );
                const expenseType =
                    process.ProcessID === 147 || process.ProcessID === 152
                        ? ExpenseRequestTypeEnum.Travel
                        : ExpenseRequestTypeEnum.Expense;

                this.log.debug({
                    type: 'ExpenseListFeatureComponent:refreshExpenseListAfterSave',
                    keepNbr,
                    priorMax,
                    expenseType,
                    employeeListID: dr?.EmployeeListID,
                    startDate: dateFilters?.startDate,
                    endDate: dateFilters?.endDate
                });

                this.store.dispatch(
                    new fromExpense.LoadExpenseSummaries({
                        startDate: dateFilters?.startDate,
                        endDate: dateFilters?.endDate,
                        tasks: null,
                        expenseItems: null,
                        employeeListID: dr?.EmployeeListID,
                        currentEmployeeListID: user?.EmployeeListID,
                        expenseType
                    })
                );

                this.dispatcher
                    .pipe(
                        ofType(fromExpense.ActionTypes.SetExpenseSummaries),
                        take(1),
                        takeWhile(() => this.active)
                    )
                    .subscribe((action: fromExpense.SetExpenseSummaries) => {
                        const items = action.payload || [];
                        let selected: IExpenseSummary = null;
                        if (keepNbr != null) {
                            selected =
                                items.find(x => x.ExpReportNbr === keepNbr) ||
                                null;
                        } else {
                            const reportDay = priorSummary?.ReportDate
                                ? moment(priorSummary.ReportDate).format(
                                      'YYYY-MM-DD'
                                  )
                                : null;
                            const created = items
                                .filter(
                                    x =>
                                        (x.ExpReportNbr || 0) > priorMax &&
                                        (!reportDay ||
                                            moment(x.ReportDate).format(
                                                'YYYY-MM-DD'
                                            ) === reportDay)
                                )
                                .sort(
                                    (a, b) =>
                                        (b.ExpReportNbr || 0) -
                                        (a.ExpReportNbr || 0)
                                );
                            selected = created[0] || null;
                        }

                        if (!selected) {
                            this.log.warn({
                                type: 'ExpenseListFeatureComponent:refreshExpenseListAfterSave:noSelection',
                                keepNbr,
                                priorMax,
                                itemCount: items.length
                            });
                            return;
                        }

                        this.popupSummary = selected;
                        this.selectedExpenseSummarySubject.next(selected);
                        // Force line/receipt reload even when ExpReportNbr is unchanged
                        this.selectExpense(selected);
                        this.selectSavedDetailAfterReload(selected.ExpReportNbr);
                        this.store.dispatch(
                            new fromExpense.LoadExpenseSummary({
                                expenseNbr: selected.ExpReportNbr,
                                EmployeeListID: dr?.EmployeeListID
                            })
                        );
                    });
            });
    }

    /**
     * After entry save, select the saved detail line (and therefore its receipt)
     * once lines/receipts finish reloading.
     */
    private selectSavedDetailAfterReload(expReportNbr: number): void {
        if (
            !this.pendingSelectNewestLine &&
            this.pendingSelectSequenceId == null
        ) {
            return;
        }
        const wantSeq = this.pendingSelectSequenceId;
        const wantNewest = this.pendingSelectNewestLine;
        this.pendingSelectSequenceId = null;
        this.pendingSelectNewestLine = false;

        combineLatest([
            this.dispatcher.pipe(
                ofType(fromExpense.ActionTypes.SetExpenseLines),
                take(1)
            ),
            this.dispatcher.pipe(
                ofType(fromExpense.ActionTypes.SetExpenseReceipts),
                take(1)
            )
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(
                ([linesAction, _receiptsAction]: [
                    fromExpense.SetExpenseLines,
                    fromExpense.SetExpenseReceipts
                ]) => {
                    const lines = (linesAction.payload || []).filter(
                        x => x.ExpReportNbr === expReportNbr
                    );
                    if (!lines.length) {
                        return;
                    }
                    let line =
                        wantSeq != null
                            ? lines.find(x => x.SequenceId === wantSeq)
                            : null;
                    if (!line && wantNewest) {
                        line = [...lines].sort(
                            (a, b) =>
                                (b.SequenceId || 0) - (a.SequenceId || 0)
                        )[0];
                    }
                    if (line) {
                        this.selectedDetailSubject.next(line);
                    }
                }
            );
    }

    loadLookups() {
        this.store.dispatch(new fromLookups.LoadPerDiemTypes());
        this.store.dispatch(new fromLookups.LoadLocales());
        this.store.dispatch(new fromLookups.LoadTravelTypes());
        this.store.dispatch(new fromLookups.LoadExpenseItems());
        this.store.dispatch(new fromLookups.LoadCountries());
        this.store.dispatch(new fromLookups.LoadAreas());
        this.store.dispatch(new fromExpense.LoadTravelMode());
        this.store.dispatch(new fromExpense.LoadTripLegTypes());
        this.store.dispatch(
            fromSettings.getCompanySetting({ SettingType: 'PurposeRequired' })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: fromSettings.CompanySettingEnum.ExternalEmployeeSource
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: fromSettings.CompanySettingEnum.ReimburseViaPayroll
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'ExpenseReportLagDays'
            })
        );
    }
    setupSubscriptions() {
        this.superUserSubject
            .pipe(
                takeWhile(() => this.active),
                startWith(false),
                withLatestFrom(this.userInfo$)
            )
            .subscribe(([isSuperUser, userInfo]) => {
                this.superUser = isSuperUser;
                if (!isSuperUser) {
                    this.emitDirectReport(
                        userInfo as unknown as IDirectReport
                    );
                }
            });
        this.ProcessSubject.pipe(takeWhile(() => this.active)).subscribe(
            process => {
                if (process.ProcessID === 147 || process.ProcessID === 152) {
                    this.expenses$ = this.store.pipe(
                        select(fromExpense.getTravelRequestSummaries),
                        distinctUntilChanged(),
                        debounceTime(500),
                        share(),
                        tap(expenses =>
                            this.log.verbose({
                                type: 'ExpenseListFeatureComponent.expenses$(travel)',
                                expenses
                            })
                        )
                    );
                } else {
                    this.expenses$ = this.store.pipe(
                        select(fromExpense.getExpenseSummaries),
                        // distinctUntilChanged(),
                        // debounceTime(500),
                        share(),
                        tap(expenses =>
                            this.log.verbose({
                                type: 'ExpenseListFeatureComponent.expenses$(expense)-1',
                                expenses
                            })
                        )
                        // map(expenses =>
                        //     expenses.map(expense => ({
                        //         ...expense,
                        //         ExpenseReportStatus: expense.RequiresOversign
                        //             ? ExpenseStatus.SuperUser
                        //             : expense.ExpenseReportStatus
                        //     }))
                        // ),
                        // tap(expenses =>
                        //     this.log.verbose({
                        //         type: 'ExpenseListFeatureComponent.expenses$(expense)-2',
                        //         expenses
                        //     })
                        // )
                    );
                }

                // this.loading$ = merge(
                //     of(false),
                //     timer(1000).pipe(takeUntil(this.expenses$), mapTo(true)),
                //     combineLatest([this.expenses$, timer(1000)]).pipe(mapTo(false))
                // );
            }
        );

        const getNewExpenses$ = combineLatest([
            this.userInfo$.pipe(
                filter(ui => !!ui.EmployeeListID),
                tap(ui =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:userinfo',
                        ui
                    })
                ),
                map(x =>
                    x.IsSuperUser ? { ...x, EmployeeListID: '' } : { ...x }
                ),
                distinctUntilKeyChanged('EmployeeListID')
            ),
            this.dateFilterSubject.pipe(
                filter((x): x is { startDate: Date; endDate: Date } => x != null),
                tap(({ startDate, endDate }) =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:dateFilterSubject',
                        startDate,
                        endDate
                    })
                ),
                distinctUntilChanged((a, b) => {
                    const result =
                        moment(a.startDate).isSame(b.startDate, 'day') &&
                        moment(a.endDate).isSame(b.endDate, 'day');
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:dateFilterSubject:distinctUntilChanged',
                        a,
                        b,
                        result
                    });
                    return result;
                })
            ),
            this.directReportsSubject.pipe(
                tap(dr =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:directReportsSubject',
                        dr
                    })
                ),
                startWith({ EmployeeListID: '' } as IDirectReport),
                distinctUntilKeyChanged('EmployeeListID')
            ),
            this.process$.pipe(
                tap(process =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:process$',
                        process
                    })
                )
            )
            // Dialog open/close must NOT reload summaries or reset selection.
            // these 2 not currently used
            // this.taskFilterSubject.pipe(startWith(null)),
            // this.expenseItemSubject.pipe(startWith(null))
        ]);

        getNewExpenses$
            .pipe(
                takeWhile(() => this.active),
                // distinctUntilChanged(),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:tap',
                        x
                    })
                )
            )
            .subscribe(
                ([
                    userInfo,
                    { startDate, endDate },
                    directReport,
                    process
                    // tasks,
                    // expenseItems
                ]) => {
                    if (this.editing) {
                        this.log.debug({
                            type: 'ExpenseListFeatureComponent:getNewExpenses$:skippedWhileEditing'
                        });
                        return;
                    }
                    this.getNewExpenses(
                        userInfo,
                        startDate,
                        endDate,
                        directReport,
                        null,
                        null,
                        process.ProcessID === 147 || process.ProcessID === 152
                            ? ExpenseRequestTypeEnum.Travel
                            : ExpenseRequestTypeEnum.Expense
                        // tasks,
                        // expenseItems
                    );
                }
            );
        combineLatest([
            this.process$.pipe(
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:process$:take(1)',
                        x
                    })
                )
            ),
            getNewExpenses$.pipe(
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:take(1)',
                        x
                    })
                )
            ),
            this.dispatcher.pipe(
                ofType<fromExpense.SetExpenseSummaries>(
                    fromExpense.ActionTypes.SetExpenseSummaries
                ),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:SetExpenseSummaries:take(1)',
                        x
                    })
                )
            )
        ])
            .pipe(
                filter(([process, ,]) => !!process),
                filter(
                    ([process, newexpenses, setSummaries]) =>
                        process.ProcessID === 88 ||
                        process.ProcessID === 86 ||
                        process.ProcessID === 152 ||
                        process.IsSuperUserProcess
                ),
                take(1),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:getNewExpenses$:setExpenseSummaries:take(1)',
                        x
                    })
                )
            )
            .subscribe(() => {
                this.emitDirectReport({
                    EmployeeListID:
                        ExpenseListFeatureComponent.NO_DIRECT_REPORT_ID
                } as IDirectReport);
                this.store.dispatch(new fromExpense.SetExpenseSummaries([]));
            });

        const expenseReportLagDaysSetting$ = this.store.pipe(
            select(fromSettings.selectEntity('ExpenseReportLagDays'))
        );

        combineLatest([
            this.userInfo$.pipe(
                filter(x => !!x.EmployeeListID)
            ),
            expenseReportLagDaysSetting$
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([userInfo, setting]) => {
                if (this.isExpenseSuperUser()) {
                    return;
                }
                const lagDays =
                    setting?.SettingValue != null &&
                    setting.SettingValue !== ''
                        ? parseInt(String(setting.SettingValue), 10)
                        : 0;
                const lag = isNaN(lagDays) ? 0 : lagDays;

                const baseStart = userInfo.UserEntryStartDate || new Date();
                const startDate = moment(baseStart)
                    .subtract(lag, 'days')
                    .toDate();

                const endDate = userInfo.UserEntryEndDate;

                this.dateFilterSubject.next({
                    startDate,
                    endDate
                });
            });

        combineLatest([
            this.userInfo$.pipe(filter(x => !!x.EmployeeListID)),
            this.dateFilterSubject,
            this.directReportsSubject
        ])
            .pipe(
                takeWhile(() => this.active),
                filter(
                    (
                        args
                    ): args is [
                        IUserInfo,
                        { startDate: Date; endDate: Date },
                        fromLookups.IDirectReport
                    ] => args[1] != null
                )
            )
            .subscribe(([userInfo, dateFilters, dr]) => {
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:userInfo$,dateFilters',
                    userInfo,
                    dateFilters
                });
                this.currentUser = userInfo;
                this.store.dispatch(
                    new fromExpense.LoadUserPaymentMethods(
                        dr.EmployeeListID || userInfo.EmployeeListID
                    )
                );
                this.store.dispatch(
                    new fromExpense.LoadExpenseReports(
                        this.getProcessRole(this.process.ProcessID, userInfo),
                        true,
                        dateFilters.startDate,
                        dateFilters.endDate
                    )
                );
            });

        // add new expense report
        this.addExpenseSummarySubject
            .pipe(takeWhile(() => this.active))
            .subscribe(({ summary, travelTypeId }) => {
                // New report entry: clear prior selection so Detail is empty until save/select.
                this.selectedExpenseSummarySubject.next(null);
                this.store.dispatch(new fromExpense.ResetSelectedExpense());
                this.store.dispatch(new fromLookups.SetLocations([]));
                this.store.dispatch(
                    new fromExpense.EditExpenseSummary(summary)
                );
                this.store.dispatch(
                    new fromExpense.EditExpenseDetail({
                        ExpReportNbr: summary.ExpReportNbr,
                        TravelTypeID: travelTypeId,
                        ExpenseID: -1
                    })
                );
                this.store.dispatch(
                    new fromExpense.EditExpenseReceipt({
                        ExpenseRptNbr: summary.ExpReportNbr
                    })
                );
                this.store.dispatch(
                    new fromExpense.EditExpenseReceiptAttachment(null)
                );
                this.store.dispatch(new fromExpense.SetTravelRates(null));
                this.store.dispatch(new fromExpense.SelectTravelRate(null));
                this.popupCanEdit = true;
                if (this.shouldOpenExpenseEditPopup(summary)) {
                    this.ExpensePopupSubject.next({
                        travelTypeId,
                        summary,
                        detail: {
                            ExpReportNbr: summary.ExpReportNbr,
                            TravelTypeID: travelTypeId,
                            ExpenseID: -1
                        }
                    });
                } else {
                    this.log.debug({
                        type: 'ExpenseListFeatureComponent:addExpenseSummarySubject:skipPopup',
                        expReportNbr: summary?.ExpReportNbr,
                        reason: 'cannotOpenExpenseEditPopup'
                    });
                }
            });

        // add line to existing selected expense report
        this.addExpenseLineSubject
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.expenseLines$.pipe(startWith([] as IExpenseLine[])))
            )
            .subscribe(([{ summary, travelTypeId }, lines]) => {
                const expReportNbr = summary?.ExpReportNbr;
                if (expReportNbr == null || expReportNbr <= 0) {
                    this.log.warn({
                        type: 'ExpenseListFeatureComponent:addExpenseLineSubject',
                        message: 'Cannot add line without an existing ExpReportNbr',
                        summary
                    });
                    return;
                }
                // Keep selection on this summary for the whole add-line flow.
                this.selectedExpenseSummarySubject.next(summary);
                const prior = (lines || [])
                    .filter(l => l?.ExpReportNbr === expReportNbr)
                    .slice(-1)[0];
                // New line: carry Purpose from prior line only — Task / Expense Item start blank.
                const detail: IExpenseLine = {
                    ExpReportNbr: expReportNbr,
                    TravelTypeID: travelTypeId,
                    ExpenseID: -1,
                    Purpose: prior?.Purpose
                };
                this.store.dispatch(new fromLookups.SetLocations([]));
                this.store.dispatch(
                    new fromExpense.EditExpenseSummary(summary)
                );
                this.store.dispatch(new fromExpense.EditExpenseDetail(detail));
                this.store.dispatch(
                    new fromExpense.EditExpenseReceipt({
                        ExpenseRptNbr: expReportNbr
                    })
                );
                this.store.dispatch(
                    new fromExpense.EditExpenseReceiptAttachment(null)
                );
                this.store.dispatch(new fromExpense.SetTravelRates(null));
                this.store.dispatch(new fromExpense.SelectTravelRate(null));
                this.popupCanEdit = this.computeCanEditForSummary(summary).canEdit;
                if (this.shouldOpenExpenseEditPopup(summary)) {
                    this.ExpensePopupSubject.next({
                        travelTypeId,
                        summary,
                        detail
                    });
                } else {
                    this.log.debug({
                        type: 'ExpenseListFeatureComponent:addExpenseLineSubject:skipPopup',
                        expReportNbr,
                        reason: 'cannotOpenExpenseEditPopup'
                    });
                }
            });

        // selecting summary — only react to real row selections (non-null).
        // distinctUntilChanged by ExpReportNbr so re-emitting the same report
        // (e.g. after dialog close) does not wipe/reload lines unnecessarily.
        this.selectedExpenseSummarySubject
            .pipe(
                takeWhile(() => this.active),
                filter(x => !!x?.ExpReportNbr && x.ExpReportNbr > 0),
                distinctUntilChanged(
                    (a, b) => a?.ExpReportNbr === b?.ExpReportNbr
                ),
                debounceTime(500),
                withLatestFrom(this.directReportsSubject),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:selectedExpenseSummarySubject',
                        x
                    })
                )
            )
            .subscribe(([summary, dr]) => {
                this.selectExpense(summary);
                this.store.dispatch(
                    new fromExpense.LoadExpenseSummary({
                        expenseNbr: summary.ExpReportNbr,
                        EmployeeListID: dr.EmployeeListID
                    })
                );
            });

        this.store
            .pipe(
                select(fromExpense.getEditingExpenseReceipt),
                takeWhile(() => this.active)
            )
            .subscribe(receipt => {
                if (receipt) {
                    this.store.dispatch(
                        new fromExpense.LoadExpenseReceiptAttachment(receipt)
                    );
                }
            });

        // edit summary
        this.editExpenseSummarySubject
            .pipe(
                debounceTime(500),
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:editExpenseSummarySubject(outer)',
                        x
                    })
                ),
                switchMap(summary =>
                    combineLatest([
                        of(summary),
                        this.expenseLines$.pipe(
                            tap(x =>
                                this.log.verbose({
                                    type: 'ExpenseListFeatureComponent:editExpenseSummarySubject:expenseDetails$',
                                    x,
                                    test: !!(x && x.length)
                                })
                            ),
                            tap(x =>
                                this.log.verbose({
                                    type: 'ExpenseListFeatureComponent:editExpenseSummarySubject:expenseDetails$',
                                    x,
                                    test: x.every(
                                        y =>
                                            y.ExpReportNbr ===
                                            summary.ExpReportNbr
                                    )
                                })
                            ),
                            filter(x => !!(x && x.length)),
                            filter(details =>
                                details.every(
                                    x => x.ExpReportNbr === summary.ExpReportNbr
                                )
                            )
                        ),
                        this.expenseReceipts$
                    ]).pipe(take(1))
                ),
                withLatestFrom(this.directReportsSubject),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:editExpenseSummarySubject(inner)',
                        x
                    })
                )
            )
            .subscribe(([[summary, details, receipts], directReport]) => {
                const detail = details
                    .map(x => ({ ...x }))
                    .sort((a, b) => a.SequenceId - b.SequenceId)[0];
                let receipt;
                if (detail) {
                    receipt =
                        receipts &&
                        receipts
                            .filter(x => x.Sequenceid === detail.SequenceId)
                            .map(x => ({ ...x }))
                            .sort((a, b) => a.Sequenceid - b.Sequenceid)[0];
                }
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:editExpenseSummarySubject:subscribe',
                    summary,
                    details,
                    detail,
                    receipts,
                    receipt,
                    directReport
                });
                this.loadExpensePopup(summary, detail, receipt, directReport);
            });

        // edit detail
        this.editDetailSubject
            .pipe(
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:editDetailSubject(outer)',
                        x
                    })
                ),
                withLatestFrom(
                    this.expenseReceipts$,
                    this.selectedExpenseSummarySubject, // .pipe(filter(x => x.ExpReportNbr === detail.ExpReportNbr))
                    this.directReportsSubject
                )
            )
            .subscribe(([detail, receipts, summary, directReport]) => {
                // this.selectExpense(summary);
                const receipt = receipts
                    .filter(x => x.Sequenceid === detail.SequenceId)
                    .map(x => ({ ...x }))
                    .sort((a, b) => a.Sequenceid - b.Sequenceid)[0];
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:editDetailSubject:subscribe',
                    receipt,
                    detail,
                    summary,
                    directReport
                });
                this.loadExpensePopup(summary, detail, receipt, directReport);
            });

        // edit receipt
        this.editReceiptSubject
            .pipe(
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'ExpenseListFeatureComponent:editReceiptSubject',
                        x
                    })
                ),
                withLatestFrom(
                    this.selectedExpenseSummarySubject,
                    this.expenseLines$,
                    this.directReportsSubject
                )
            )
            .subscribe(([receipt, summary, details, directReport]) => {
                this.selectExpense(summary);
                const detail = details.find(
                    x => x.ExpenseID === receipt.ExpenseID
                );
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:editReceiptSubject:subscribe',
                    receipt,
                    details,
                    detail,
                    summary,
                    directReport
                });
                this.loadExpensePopup(summary, detail, receipt, directReport);
            });

        this.deleteExpenseSubject
            .pipe(takeWhile(() => this.active))
            .subscribe(x => {
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:deleteExpenseSubject:subscribe:tap',
                    x
                });
                this.store.dispatch(new fromExpense.DeleteExpenseSummary(x));
            });
        this.deleteDetailSubject
            .pipe(takeWhile(() => this.active))
            .subscribe(x => {
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:deleteDetailSubject:subscribe:tap',
                    x
                });
                this.store.dispatch(new fromExpense.DeleteExpenseDetail(x));
            });
        this.deleteReceiptSubject
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.selectedDetail$)
            )
            .subscribe(([receipt, detail]) => {
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:deleteReceiptSubject:subscribe:tap',
                    receipt,
                    detail
                });
                // TODO: can this logic be moved to the effects?
                this.store.dispatch(
                    new fromExpense.DeleteExpenseReceipt(receipt)
                );
                if (
                    detail &&
                    detail.TravelTypeID === TravelTypeEnum.Receipt &&
                    detail.ReceiptCount === 1
                ) {
                    this.store.dispatch(
                        new fromExpense.RemoveExpenseDetail(detail)
                    );
                }
            });
        this.oversignExpenseSubject
            .pipe(takeWhile(() => this.active))
            .subscribe(x => {
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:oversignExpenseSubject:subscribe:tap',
                    x
                });
                this.store.dispatch(new fromExpense.OversignExpenseSummary(x));
            });
        this.ConvertExpenseSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfo$),
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:ConvertExpenseSubject',
                    x
                })
            )
        ).subscribe(([summaries, userInfo]) => {
            this.store.dispatch(
                new fromExpense.ConvertExpense({
                    summary: summaries,
                    roleId: this.getProcessRole(
                        this.process.ProcessID,
                        userInfo
                    )
                })
            );
        });
        this.ValidateExpenseSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfo$),
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:ValidateExpenseSubject',
                    x
                })
            )
        ).subscribe(([summaries, userInfo]) => {
            this.store.dispatch(new fromExpense.ClearMessages());
            this.store.dispatch(
                new fromExpense.ValidateExpense({
                    summaries,
                    roleId: this.getProcessRole(
                        this.process.ProcessID,
                        userInfo
                    )
                })
            );
        });
        this.ApproveExpenseSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfo$),
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:ApproveExpenseSubject',
                    x
                })
            )
        ).subscribe(([summaries, userInfo]) => {
            this.store.dispatch(new fromExpense.ClearMessages());
            this.store.dispatch(
                new fromExpense.ApproveExpense({
                    summaries,
                    roleId: this.getProcessRole(
                        this.process.ProcessID,
                        userInfo
                    )
                })
            );
        });
        this.UnvalidateExpenseSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfo$),
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:UnvalidateExpenseSubject',
                    x
                })
            )
        ).subscribe(([summaries, userInfo]) => {
            this.store.dispatch(new fromExpense.ClearMessages());
            this.store.dispatch(
                new fromExpense.UnvalidateExpense({
                    summaries,
                    roleId: this.getProcessRole(
                        this.process.ProcessID,
                        userInfo
                    )
                })
            );
        });
        this.UnapproveExpenseSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfo$),
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:UnapproveExpenseSubject',
                    x
                })
            )
        ).subscribe(([summaries, userInfo]) => {
            this.store.dispatch(new fromExpense.ClearMessages());
            this.store.dispatch(
                new fromExpense.UnapproveExpense({
                    summaries,
                    roleId: this.getProcessRole(
                        this.process.ProcessID,
                        userInfo
                    )
                })
            );
        });
        this.DisapproveExpenseSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfo$),
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListFeatureComponent:DisapproveExpenseSubject',
                    x
                })
            )
        ).subscribe(([summaries, userInfo]) => {
            this.store.dispatch(new fromExpense.ClearMessages());
            this.store.dispatch(
                new fromExpense.DisapproveExpense({
                    summaries,
                    roleId: this.getProcessRole(
                        this.process.ProcessID,
                        userInfo
                    )
                })
            );
        });
        this.ExpensePopupSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(
                this.directReportsSubject.pipe(
                    startWith(null as IDirectReport | null)
                ),
                this.userInfo$.pipe(filter(x => !!x?.EmployeeListID)),
                this.superUserSubject.pipe(startWith(false)),
                this.currentUserSubject.pipe(
                    startWith(null as IDirectReport | null)
                )
            ),
            withLatestFrom(
                this.process$.pipe(filter((p): p is Process => !!p))
            )
        ).subscribe(([[popupContext, dr, user, isSuperUser, currentUser], process]) => {
            this.popupTravelTypeId =
                popupContext?.travelTypeId || TravelTypeEnum.PerDiem;
            this.popupSummary = popupContext?.summary;
            this.popupDetail = popupContext?.detail || {
                ExpReportNbr: popupContext?.summary?.ExpReportNbr,
                TravelTypeID: this.popupTravelTypeId,
                ExpenseID: -1
            };
            const isSuper = isSuperUser || this.isExpenseSuperUser();
            let cu = this.resolveDirectReportForEntryWindow(dr, user, isSuper);
            if (
                (!cu?.MinWorkDate || !cu?.MaxWorkDate) &&
                currentUser?.MinWorkDate &&
                currentUser?.MaxWorkDate
            ) {
                cu = currentUser;
            }
            if (!cu?.EmployeeListID) {
                cu = {
                    id: user.EmployeeListID,
                    EmployeeListID: user.EmployeeListID,
                    EmployeeName: user.EmployeeName,
                    MinWorkDate: moment(user.UserEntryStartDate).toISOString(),
                    MaxWorkDate: moment(user.UserEntryEndDate).toISOString()
                } as IDirectReport;
            }
            this.showExpensePopup(cu, process);
        });
    }
    public getProcessRole(processID: number, user: IUserInfo): UserRoleEnum {
        if (!(processID && user)) {
            return 0;
        }
        let roleId;
        switch (processID) {
            case 88:
                roleId = UserRoleEnum.TaskManager;
                break;
            case 86:
            case 152:
                roleId = UserRoleEnum.Supervisor;
                break;
            default:
                roleId = user.DefaultRoleGroupID;
                break;
        }
        return roleId;
    }
    private getNewExpenses(
        user: IUserInfo,
        startDate: Date,
        endDate: Date,
        directReport: fromLookups.IDirectReport,
        tasks: Array<fromLookups.ITask>,
        expenseItems: Array<fromLookups.IExpenseItem>,
        expenseType: ExpenseRequestTypeEnum
    ) {
        this.log.verbose({
            type: 'ExpenseListFeatureComponent:getNewExpenses',
            user,
            startDate,
            endDate,
            directReport,
            tasks,
            expenseItems,
            expenseType
        });
        this.store.dispatch(new fromExpense.SelectDirectReport(directReport));
        // Date/user filter reload is the only path that clears selection besides
        // the user clicking a different Summary row.
        this.selectedExpenseSummarySubject.next(null);
        this.store.dispatch(new fromExpense.ResetSelectedExpense());
        this.store.dispatch(
            new fromExpense.LoadExpenseSummaries({
                startDate,
                endDate,
                tasks,
                expenseItems,
                employeeListID: directReport && directReport.EmployeeListID,
                currentEmployeeListID: user && user.EmployeeListID,
                expenseType
            })
        );
    }
    // Event handlers
    public onEditExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onEditExpense',
            event: $event
        });
        this.editExpenseSummarySubject.next($event);
    }
    public onAddExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onAddExpense',
            event: $event
        });
        this.addExpenseSummarySubject.next($event);
    }
    public onAddExpenseLine($event: {
        summary: IExpenseSummary;
        travelTypeId: TravelTypeEnum;
    }) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onAddExpenseLine',
            event: $event,
            expReportNbr: $event?.summary?.ExpReportNbr
        });
        this.addExpenseLineSubject.next($event);
    }
    public onShowExpenseDetail($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onShowExpenseDetail',
            event: $event,
            editing: this.editing
        });
        // Selection stays until the user picks another Summary row (or filters
        // reload via getNewExpenses). Ignore null clears from clearDependentGrids.
        if ($event == null) {
            return;
        }
        this.selectedExpenseSummarySubject.next($event);
    }
    public onDeleteExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onDeleteExpense',
            event: $event
        });
        this.deleteExpenseSubject.next($event);
    }
    public onDeleteDetail($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onDeleteDetail',
            event: $event
        });
        this.deleteDetailSubject.next($event);
    }
    public onDeleteReceipt($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onDeleteReceipt',
            event: $event
        });
        this.deleteReceiptSubject.next($event);
    }
    public onOversignExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onOversignExpense',
            event: $event
        });
        this.oversignExpenseSubject.next($event);
    }
    public onConvertExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onConvertExpense',
            event: $event
        });
        this.ConvertExpenseSubject.next($event);
    }
    public onValidateExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onValidateExpense',
            event: $event
        });
        this.ValidateExpenseSubject.next($event);
    }
    public onApproveExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onApproveExpense',
            event: $event
        });
        this.ApproveExpenseSubject.next($event);
    }
    public onUnvalidateExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onUnvalidateExpense',
            event: $event
        });
        this.UnvalidateExpenseSubject.next($event);
    }
    public onUnapproveExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onUnapproveExpense',
            event: $event
        });
        this.UnapproveExpenseSubject.next($event);
    }
    public onDisapproveExpense($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onDisapproveExpense',
            event: $event
        });
        this.DisapproveExpenseSubject.next($event);
    }
    public onEditDetail($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onEditDetail',
            event: $event
        });
        this.editDetailSubject.next($event);
    }
    public onSelectDetail($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onSelectDetail',
            event: $event
        });
        this.selectedDetailSubject.next($event);
    }
    public onEditReceipt($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onEditReceipt',
            event: $event
        });
        this.editReceiptSubject.next($event);
    }
    public onSelectReceipt($event) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onSelectReceipt',
            event: $event
        });
        this.selectedReceiptSubject.next($event);
    }
    public onDateFiltersChanged($event: { startDate: Date; endDate: Date }) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onDateFiltersChanged',
            event: $event
        });
        const current = this.dateFilterSubject.getValue();
        if (
            current &&
            moment(current.startDate).isSame($event.startDate, 'day') &&
            moment(current.endDate).isSame($event.endDate, 'day')
        ) {
            // No actual change; avoid feeding the same dates back into the inputs,
            // which was resetting the datepicker month view.
            return;
        }
        this.dateFilterSubject.next($event);
    }
    public onTaskFilterChanged($event: Array<fromLookups.ITask>) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onTaskFilterChanged',
            event: $event
        });
        this.taskFilterSubject.next($event);
    }
    public onExpenseItemFilterChanged($event: Array<fromLookups.IExpenseItem>) {
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onExpenseItemFilterChanged',
            event: $event
        });
        this.expenseItemSubject.next($event);
    }
    public onUserSelected($event: fromLookups.IDirectReport) {
        if (!$event) {
            return;
        }
        this.log.debug({
            type: 'ExpenseListFeatureComponent:onUserSelected',
            event: $event
        });
        if (
            this.isExpenseSuperUser() &&
            $event.MinWorkDate &&
            $event.MaxWorkDate
        ) {
            this.syncDateFilterFromDirectReport($event);
        }
        this.emitDirectReport(
            $event || {
                id: this.currentUser.EmployeeListID,
                EmployeeListID: this.currentUser.EmployeeListID,
                EmployeeName: this.currentUser.EmployeeName,
                IsActive: true
            }
        );
    }
    public onCanClose($event: boolean) {
        this.canClose = $event;
    }
    onAddSelectedExpense($event) {
        this.store.dispatch(new fromExpense.AddSelectedExpense($event));
    }
    onRemoveSelectedExpense($event) {
        this.store.dispatch(new fromExpense.RemoveSelectedExpense($event));
    }
    onClearSelectedExpenses($event) {
        this.store.dispatch(new fromExpense.ClearSelectedExpenses());
    }
    onShowPdbo($event: IExpenseLine) {
        this.dialogRef = this.dialogService.open(ExpensePdboFeatureComponent, {
            header: 'Expense Entry',
            closable: false,
            closeOnEscape: false,
            showHeader: false,
            modal: true,
            autoZIndex: true,
            data: {
                expenseLineId: $event.ExpenseID
            }
        });
        this.dialogRef.onClose
            .pipe(
                take(1)
                // withLatestFrom(of(1))
                // withLatestFrom(
                //     this.store.pipe(
                //         select(
                //             fromExpense.selectSummaryById($event.ExpReportNbr)
                //         )
                //     )
                // )
                // select(fromExpense.selectSummaryById($event.ExpReportNbr))
            )
            .subscribe(value => {
                this.log.verbose({
                    type: 'ExpensePdboFeatureComponent:afterClosed',
                    value
                });
                if (value) {
                    this.store.dispatch(
                        new fromExpense.UpdateExpenseLinePDBO(value)
                    );
                    //would be nice to just load the one
                    this.store.dispatch(
                        new fromExpense.LoadExpenseSummary({
                            expenseNbr: $event.ExpReportNbr,
                            EmployeeListID: this.userInfo.EmployeeListID
                        })
                    );
                    // this.store.dispatch(new fromExpense.LoadExpenseSummaries());
                    this.store.dispatch(
                        new fromExpense.LoadExpenseLines($event.ExpReportNbr)
                    );
                    this.store
                        .pipe(
                            select(
                                fromExpense.selectSummaryById(
                                    $event.ExpReportNbr
                                )
                            ),
                            skip(1),
                            take(1)
                        )
                        .subscribe(summary => {
                            this.log.verbose({
                                type: 'ExpensePdboFeatureComponent:reselect summary',
                                summary
                            });
                            this.selectedExpenseSummarySubject.next(summary);
                        });
                    //TODO: set expenseSummary$
                }
            });
    }
}
